import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Target, Hash, MessageCircle, X, Calendar, Bookmark, Share2, ThumbsUp, Copy, CheckCircle, Users, Check, TrendingUp, CheckSquare, Music, MessageSquare, ArrowRight, FileText, BarChart2 } from 'lucide-react';
import { useContentPlanStore } from '../../../../stores/contentPlanStore';
import { useUserStore } from '../../../../stores/userStore';
import { useFeedbackStore } from '../../../../stores/feedbackStore';
import { generateContentPlan } from '../../../../services/openai';
import { sanitizeHtml } from '../../../../utils/sanitize';
import toast from 'react-hot-toast';
import FeedbackPrompt from './FeedbackPrompt';

interface GeneratedPlanProps {
  answers: PlanAnswers;
  onClose: () => void;
}

export default function GeneratedPlan({ answers, onClose }: GeneratedPlanProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'download' | 'calendar' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const [copiedHashtag, setCopiedHashtag] = useState<string | null>(null);
  const [likedDays, setLikedDays] = useState<Record<string, boolean>>({});
  const { addPlan, canCreatePlan } = useContentPlanStore();
  const hasUnlimitedAccess = useUserStore((state) => state.hasUnlimitedAccess());
  const addFeedback = useFeedbackStore((state) => state.addFeedback);
  const navigate = useNavigate();

  // Parse HTML content into structured data
  const [parsedContent, setParsedContent] = useState<{
    title: string;
    sections: {
      title: string;
      content: string | string[];
      type: 'text' | 'list' | 'days';
    }[];
    days: {
      title: string;
      sections: {
        label: string;
        content: string;
        icon?: React.ReactNode;
      }[];
    }[];
  } | null>(null);

  React.useEffect(() => {
    const generatePlan = async () => {
      try {
        const generatedContent = await generateContentPlan(answers);
        setContent(generatedContent);
        
        // Parse the HTML content
        parseContent(generatedContent);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao gerar plano');
        toast.error('Erro ao gerar plano. Por favor, tente novamente.');
      } finally {
        setIsLoading(false);
      }
    };

    generatePlan();
  }, [answers]);

  const parseContent = (htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizeHtml(htmlContent), 'text/html');
    
    // Get title
    const title = doc.querySelector('h1')?.textContent || 'Plano Estratégico de Conteúdo';
    
    // Get sections
    const sections: {
      title: string;
      content: string | string[];
      type: 'text' | 'list' | 'days';
    }[] = [];
    
    // Get all h2 elements (main sections)
    const h2Elements = doc.querySelectorAll('h2');
    h2Elements.forEach(h2 => {
      const title = h2.textContent || '';
      
      // Skip the "Calendário Semanal" section as we'll handle it separately
      if (title.includes('Calendário Semanal')) return;
      
      let content: string | string[] = '';
      let type: 'text' | 'list' = 'text';
      
      // Check if next element is a paragraph or list
      let nextElement = h2.nextElementSibling;
      if (nextElement?.tagName === 'P') {
        content = nextElement.textContent || '';
      } else if (nextElement?.tagName === 'UL') {
        type = 'list';
        content = Array.from(nextElement.querySelectorAll('li')).map(li => li.textContent || '');
      }
      
      sections.push({ title, content, type });
    });
    
    // Get days
    const days: {
      title: string;
      sections: {
        label: string;
        content: string;
        icon?: React.ReactNode;
      }[];
    }[] = [];
    
    // Get all day headers
    const dayHeaders = doc.querySelectorAll('.day-header');
    dayHeaders.forEach(header => {
      const title = header.textContent || '';
      const sections: {
        label: string;
        content: string;
        icon?: React.ReactNode;
      }[] = [];
      
      // Get all task labels and their content
      let element = header.nextElementSibling;
      while (element && !element.classList.contains('day-header')) {
        if (element.classList.contains('task-label')) {
          const label = element.textContent || '';
          const content = element.nextElementSibling?.textContent || '';
          
          // Determine icon based on label
          let icon;
          if (label.includes('Objetivo')) icon = <Target className="h-5 w-5 text-indigo-600" />;
          else if (label.includes('fazer')) icon = <CheckSquare className="h-5 w-5 text-indigo-600" />;
          else if (label.includes('Música')) icon = <Music className="h-5 w-5 text-indigo-600" />;
          else if (label.includes('Legenda')) icon = <MessageSquare className="h-5 w-5 text-indigo-600" />;
          else if (label.includes('CTA')) icon = <MessageCircle className="h-5 w-5 text-indigo-600" />;
          else if (label.includes('Hashtags')) icon = <Hash className="h-5 w-5 text-indigo-600" />;
          
          sections.push({ label, content, icon });
        }
        element = element.nextElementSibling;
      }
      
      days.push({ title, sections });
    });
    
    // Set active day to first day
    if (days.length > 0 && !activeDay) {
      setActiveDay(days[0].title);
    }
    
    setParsedContent({ title, sections, days });
  };

  const handleFeedbackSubmit = async (feedback: { rating: number; comment: string; planName: string }) => {
    addFeedback({
      planId: answers.id,
      rating: feedback.rating,
      comment: feedback.comment
    });

    setShowFeedback(false);

    try {
      if (pendingAction === 'save') {
        const success = addPlan({
          id: answers.id,
          title: feedback.planName,
          content,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        if (success) {
          toast.success('Plano salvo com sucesso! Confira em "Meus Planos"');
          onClose();
          navigate('/dashboard/planos');
        } else {
          toast.error('Erro ao salvar o plano. Por favor, tente novamente.');
        }
      } else if (pendingAction === 'download') {
        const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${feedback.planName.toLowerCase().replace(/\s+/g, '-')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Plano baixado com sucesso!');
      } else if (pendingAction === 'calendar') {
        // Save plan first
        const success = addPlan({
          id: answers.id,
          title: feedback.planName,
          content,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        if (success) {
          // Navigate to calendar with import modal open
          navigate('/dashboard/calendario', { 
            state: { showImportModal: true, selectedPlanId: answers.id }
          });
          onClose();
        } else {
          toast.error('Erro ao salvar o plano. Por favor, tente novamente.');
        }
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error('Ocorreu um erro. Por favor, tente novamente.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleSave = () => {
    setPendingAction('save');
    setShowFeedback(true);
  };

  const handleDownload = () => {
    setPendingAction('download');
    setShowFeedback(true);
  };

  const handleAddToCalendar = () => {
    setPendingAction('calendar');
    setShowFeedback(true);
  };

  const handleCopyHashtag = (hashtag: string) => {
    navigator.clipboard.writeText(hashtag);
    setCopiedHashtag(hashtag);
    setTimeout(() => setCopiedHashtag(null), 2000);
    toast.success('Hashtag copiada!');
  };

  const handleLikeDay = (day: string) => {
    setLikedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
    toast.success(likedDays[day] ? 'Removido dos favoritos' : 'Adicionado aos favoritos!');
  };

  // Handle sharing content with fallback to clipboard
  const handleShare = async (text: string) => {
    try {
      // Check if Web Share API is supported and we're in a secure context
      if (navigator.share && window.isSecureContext) {
        await navigator.share({
          title: 'Kontty - Plano de Conteúdo',
          text: text
        });
        toast.success('Conteúdo compartilhado com sucesso!');
      } else {
        // Fallback to clipboard if Web Share API is not available
        await navigator.clipboard.writeText(text);
        toast.success('Conteúdo copiado para a área de transferência!');
      }
    } catch (error) {
      // Fallback to clipboard if sharing fails
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Conteúdo copiado para a área de transferência!');
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
        toast.error('Não foi possível copiar o conteúdo. Por favor, tente novamente.');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-indigo-600" />
            Plano de Conteúdo - {new Date().toLocaleDateString('pt-BR')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 bg-gradient-to-br from-indigo-50/30 to-white">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-4 border-indigo-200 border-opacity-50"></div>
                <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Gerando seu plano...</h3>
              <p className="text-gray-600">Estamos criando um plano personalizado baseado nas suas respostas</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fechar
              </button>
            </div>
          ) : parsedContent ? (
            <div className="space-y-8">
              {/* Title */}
              <div className="text-center bg-white p-6 rounded-xl shadow-sm border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/50">
                <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-800 mb-2">{parsedContent.title}</h1>
                <p className="text-gray-600">Plano personalizado baseado nas suas respostas</p>
              </div>

              {/* Overview Sections */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {parsedContent.sections.slice(0, 3).map((section, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center">
                      {index === 0 && <Target className="h-5 w-5 mr-2 text-indigo-600" />}
                      {index === 1 && <Users className="h-5 w-5 mr-2 text-indigo-600" />}
                      {index === 2 && <FileText className="h-5 w-5 mr-2 text-indigo-600" />}
                      {section.title.replace(/^\d+\.\s/, '')}
                    </h3>
                    {section.type === 'text' ? (
                      <p className="text-gray-700 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-100 shadow-sm">{section.content as string}</p>
                    ) : (
                      <ul className="space-y-2 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-100 shadow-sm">
                        {(section.content as string[]).map((item, i) => (
                          <li key={i} className="flex items-start">
                            <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              {/* Day Tabs */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <h3 className="text-lg font-semibold text-white p-4 bg-gradient-to-r from-indigo-600 to-indigo-500 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Calendário Semanal
                </h3>
                
                <div className="flex overflow-x-auto space-x-2 p-4 border-b border-gray-200 bg-gray-50">
                  {parsedContent.days.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveDay(day.title)}
                      className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all transform ${
                        activeDay === day.title
                          ? 'bg-indigo-600 text-white scale-105 shadow-md'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 hover:scale-105'
                      }`}
                    >
                      {day.title.split(':')[0]}
                    </button>
                  ))}
                </div>
                
                {/* Day Content */}
                <div className="p-4 bg-gradient-to-br from-indigo-50/30 to-white">
                  {parsedContent.days.map((day, index) => (
                    <div 
                      key={index}
                      className={activeDay === day.title ? 'block' : 'hidden'}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-800">{day.title}</h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleLikeDay(day.title)}
                            className={`p-2 rounded-full ${
                              likedDays[day.title] 
                                ? 'bg-red-100 text-red-600' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            } transition-all transform hover:scale-110`}
                            title={likedDays[day.title] ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                          >
                            <ThumbsUp className="h-5 w-5" fill={likedDays[day.title] ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            onClick={() => {
                              const text = day.sections.map(item => `${item.label}\n${item.content}`).join('\n\n');
                              navigator.clipboard.writeText(text);
                              toast.success('Conteúdo copiado para a área de transferência!');
                            }}
                            className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-all transform hover:scale-110"
                            title="Copiar conteúdo"
                          >
                            <Copy className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              const text = `${day.title}\n\n${day.sections.map(item => `${item.label}\n${item.content}`).join('\n\n')}`;
                              handleShare(text);
                            }}
                            className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-all transform hover:scale-110"
                            title="Compartilhar"
                          >
                            <Share2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {day.sections.map((section, sectionIndex) => {
                          // Special handling for hashtags
                          if (section.label.includes('Hashtags')) {
                            return (
                              <div key={sectionIndex} className="md:col-span-2 bg-white p-4 rounded-lg border border-indigo-100 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center space-x-2 mb-3">
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <Hash className="h-4 w-4 text-indigo-600" />
                                  </div>
                                  <h4 className="font-medium text-gray-900">{section.label}</h4>
                                </div>
                                <div className="flex flex-wrap gap-2 bg-gradient-to-r from-indigo-50/50 to-white p-3 rounded-lg">
                                  {section.content.split(' ')
                                    .filter(tag => tag.startsWith('#'))
                                    .map((tag, tagIndex) => (
                                      <button
                                        key={tagIndex}
                                        onClick={() => handleCopyHashtag(tag)}
                                        className="group relative px-3 py-1.5 bg-white text-indigo-600 rounded-full border border-indigo-100 hover:bg-indigo-100 transition-all transform hover:scale-105 shadow-sm flex items-center space-x-1"
                                      >
                                        <span>{tag}</span>
                                        {copiedHashtag === tag ? (
                                          <CheckCircle className="h-4 w-4 text-green-500 ml-1" />
                                        ) : (
                                          <Copy className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                                        )}
                                      </button>
                                    ))}
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div key={sectionIndex} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.01]">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                  {section.icon || <Check className="h-4 w-4 text-indigo-600" />}
                                </div>
                                <h4 className="font-medium text-gray-900">{section.label}</h4>
                              </div>
                              <p className="text-gray-700 bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-100 ml-10">{section.content}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {parsedContent.sections.slice(3).map((section, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center">
                      {section.title.includes('Engajamento') && <TrendingUp className="h-5 w-5 mr-2 text-indigo-600" />}
                      {section.title.includes('Hashtags') && <Hash className="h-5 w-5 mr-2 text-indigo-600" />}
                      {section.title.includes('Métricas') && <BarChart2 className="h-5 w-5 mr-2 text-indigo-600" />}
                      {section.title.replace(/^\d+\.\s/, '')}
                    </h3>
                    {section.type === 'text' ? (
                      <p className="text-gray-700 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-100 shadow-sm">{section.content as string}</p>
                    ) : (
                      <ul className="space-y-2 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-100 shadow-sm">
                        {(section.content as string[]).map((item, i) => (
                          <li key={i} className="flex items-start">
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0">
                              <Check className="h-3 w-3 text-green-600" />
                            </div>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">Não foi possível carregar o plano. Por favor, tente novamente.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Plano Personalizado</p>
              <p className="text-xs text-gray-500">Gerado em {new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              disabled={isLoading || !!error}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar HTML
            </button>
            <button
              onClick={handleAddToCalendar}
              disabled={isLoading || !!error}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Adicionar ao Calendário
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors transform hover:scale-105"
            >
              Fechar
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !!error}
              className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center transform hover:scale-105 shadow-sm"
            >
              Salvar Plano
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {showFeedback && (
        <FeedbackPrompt
          onSubmit={handleFeedbackSubmit}
          onClose={() => {
            setShowFeedback(false);
            setPendingAction(null);
          }}
          message={
            pendingAction === 'save'
              ? "Por favor, compartilhe sua opinião antes de salvar o plano."
              : pendingAction === 'calendar'
              ? "Por favor, compartilhe sua opinião antes de adicionar ao calendário."
              : "Sua opinião é importante! Por favor, deixe sua avaliação antes de baixar o plano."
          }
        />
      )}
    </div>
  );
}